import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import BackIcon from '../../assets/icons/back-icon.svg';
import { ReactComponent as BotIcon } from '../../assets/icons/bot-icon.svg';

import { PillButton } from '../../../components/Button/ActionButtons';
import useUserDoc from '../../../CoachTools/hooks/useUserDoc';
import useSilenceToggle from '../../../CoachTools/hooks/useSilenceToggle';
import ChatChannel from '../../Model/ChatChannel';
import ChannelUnreadCount from '../../Model/ChannelUnreadCount';
import ChatContext from '../../context/ChatContext';
import ChatChannelContext from '../../context/ChatChannelContext';
import { ChatPage } from '../../utils/chatPage';
import ChatUserProfilePicture from '../ChatUserProfilePicture';
import { Event } from '../../utils/events';
import { ChannelBuckets } from '../../utils/channelBuckets';
import ChannelActionMenu from '../ChannelPreview/ChannelActionMenu';
import ChannelDocumentsModal from '../ChannelDocumentsModal';
import { variants } from '../Tag';

import texts from './texts.json';
import {
  Container,
  ActionButtonsContainer,
  TextContainer,
  StyledLink,
  BackButton,
  StyledBackIcon,
  IconContainer,
  StyledChannelPopoverIcon,
  StyledTag,
  SmartContextButton,
  EndActions,
  ThreadNoticePill,
  SilenceToggleContainer,
  StyledCheckbox,
  StyledFormHelperText,
  StyledFolderIcon,
} from './styles';

const ChannelHeader = ({
  channel,
  isWebView,
  children,
}) => {
  const {
    setCurrentChatPage,
    sessionStore,
    logEvent,
    authUser,
    chatUser,
    setActiveChannelId,
    setActiveUserId,
    smartContextOpen,
    setSmartContextOpen,
    isSmartContextEnabled,
  } = useContext(ChatContext);
  const {
    navigateToClientPage,
    renderHeaderExtras,
    activeChannel,
    setActiveChannel,
    unseenThreadCount,
    recentThreadRows,
    isUnreadTruncated,
    isThreadInboxOpen,
    setIsThreadInboxOpen,
  } = useContext(ChatChannelContext);
  const {
    isCoachOrAdmin,
    isCoachAssistant,
    isCoach,
    isCoachAndCoachAssistant,
  } = sessionStore;

  const {
    name,
    profileImage,
    isOnline,
    userId,
    coachId,
    isHidden,
    isArchived,
    claimedBy,
    claimedByUsername,
    isClaimValid,
    isAIFriendEnabled,
  } = channel;

  const { userDoc: clientUserDoc } = useUserDoc(userId, { viewAsCoachId: coachId });
  const { isSilenced, handleSilenceToggle } = useSilenceToggle(clientUserDoc);

  const showClaim = isCoachAssistant && !!claimedBy && isClaimValid && !isAIFriendEnabled;
  const isSelfClaimed = !!claimedBy && claimedBy === authUser.id;
  /*
    When the seatbelt binds, the count is a floor rather than the total, so the pill says `N+`. The
    pill is the surface the ticket meant by "nothing on screen says so", and the one place the
    number is read without opening anything. Mobile's chip carries the same `N+`.
  */
  const threadNoticeText = unseenThreadCount > 0
    ? format(texts.threadNotice, {
      count: isUnreadTruncated ? `${unseenThreadCount}+` : unseenThreadCount,
      threads: unseenThreadCount === 1 && !isUnreadTruncated ? texts.thread : texts.threads,
    })
    : texts.threadInbox;
  /*
    Mounted for as long as the channel has threads at all, not only while some are unread: clearing
    the last one used to unmount the pill, which took the Recent tab and the empty state with it.
  */
  const hasThreadInbox = recentThreadRows.length > 0;

  const isBotIconVisible = !isCoach || isCoachAndCoachAssistant || isSmartContextEnabled;

  const isClientLoggedIn = !isCoachOrAdmin;
  const {
    unreadCountDoc,
  } = useContext(ChatChannelContext);
  const [anchorEl, setAnchorEl] = useState();
  const [isPopperOpen, setIsPopperOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);

  if (isClientLoggedIn) {
    return (
      <Container>
        <TextContainer $width={100}>
          {texts.chatWithCoach}
        </TextContainer>
      </Container>
    );
  }

  const selectedBucket = () => {
    if (!isHidden && !isArchived) {
      return ChannelBuckets.ACTIVE;
    }
    return ChannelBuckets.ALL;
  };

  const onMarkReadUnread = async () => {
    if (!unreadCountDoc?.unreadCount) {
      if (Object.keys(unreadCountDoc || {}).length === 0) {
        const unreadDoc = await ChannelUnreadCount.getUnreadCountDocForChannelByUser(chatUser.id, activeChannel.id);
        await unreadDoc.increaseUnreadCount();
      } else {
        await unreadCountDoc.increaseUnreadCount();
      }
    } else {
      const unreadCountAtDismiss = unreadCountDoc.unreadCount;
      await unreadCountDoc.setUnreadCountToZero();
      logEvent(Event.CHAT_MARKED_AS_READ_MANUAL, {
        readBy: authUser.id,
        thread_id: activeChannel?.id || '',
        user_role: isCoachAssistant ? 'AC' : 'coach',
        unread_count_at_dismiss: unreadCountAtDismiss,
      });
    }
  };

  const onArchiveUnarchive = async () => {
    const data = { actionedBy: authUser.id, channelId: activeChannel?.id || '' };
    if (isArchived) {
      channel.unarchive(authUser.id);
      logEvent(Event.UNARCHIVE_CHANNEL, data);
    } else {
      if (unreadCountDoc && unreadCountDoc.unreadCount) {
        await unreadCountDoc.setUnreadCountToZero();
      }
      channel.archive(authUser.id);
      logEvent(Event.ARCHIVE_CHANNEL, data);

      // reset the active channel on action.
      if (!isWebView) setCurrentChatPage(ChatPage.ChannelList);
      setActiveChannel(null);
      setActiveChannelId('');
      setActiveUserId('');
    }
  };

  const handleAction = (event) => {
    setAnchorEl(event.currentTarget);
    setIsPopperOpen((prev) => !prev);
  };

  return (
    <Container>
      {!isWebView && (
        <BackButton>
          <StyledBackIcon
            onClick={() => {
              // Reset the single channel data
              setActiveChannel(null);
              setActiveChannelId('');
              setActiveUserId('');

              setCurrentChatPage(ChatPage.ChannelList);
            }}
            src={BackIcon}
          />
        </BackButton>
      )}
      <ChatUserProfilePicture image={profileImage} isOnline={isOnline} name={name} />
      <TextContainer>
        <StyledLink onClick={() => navigateToClientPage(userId)}>{name}</StyledLink>
      </TextContainer>
      {!isAIFriendEnabled && (
        <ActionButtonsContainer>
          {renderHeaderExtras && renderHeaderExtras(userId)}
          <PillButton
            size="small"
            onClick={() => setIsDocumentsModalOpen(true)}
            icon={<StyledFolderIcon />}
            variant="info"
          >
            {texts.docs}
          </PillButton>
          <PillButton onClick={onMarkReadUnread} variant="info" size="small">
            {!unreadCountDoc?.unreadCount ? texts.markUnread : texts.markRead}
          </PillButton>
          <PillButton onClick={onArchiveUnarchive} variant="info" size="small">
            {isArchived ? texts.unarchive : texts.archive}
          </PillButton>
          {isCoachAssistant && (
            <SilenceToggleContainer>
              <StyledCheckbox
                checked={isSilenced}
                onChange={handleSilenceToggle}
                disabled={!clientUserDoc}
                size="small"
              />
              <StyledFormHelperText>{texts.silenceBroadcasts}</StyledFormHelperText>
            </SilenceToggleContainer>
          )}
        </ActionButtonsContainer>
      )}
      {isAIFriendEnabled && (
        <StyledTag variant={variants.SUCCESS}>
          {texts.aiFriend}
        </StyledTag>
      )}
      {showClaim && (
        <StyledTag variant={isSelfClaimed ? variants.SUCCESS : variants.WARNING}>
          {isSelfClaimed ? texts.selfClaimed : format(texts.claimed, { name: claimedByUsername })}
        </StyledTag>
      )}
      {isWebView && (
        <EndActions>
          {hasThreadInbox && (
            <ThreadNoticePill
              type="button"
              $muted={unseenThreadCount === 0 && !isUnreadTruncated}
              onClick={() => {
                if (!isThreadInboxOpen) {
                  logEvent(Event.THREAD_INBOX_OPENED, {
                    userId: authUser?.id,
                    timestamp: new Date().toISOString(),
                    channelId: activeChannel?.id,
                    unreadCount: unseenThreadCount,
                  });
                }
                setIsThreadInboxOpen((isOpen) => !isOpen);
              }}
            >
              {threadNoticeText}
            </ThreadNoticePill>
          )}
          {!smartContextOpen && isBotIconVisible && (
            <SmartContextButton
              type="button"
              onClick={() => {
                setSmartContextOpen(true);
                logEvent(Event.SMART_CONTEXT_SIDEBAR_OPENED, {
                  userId: authUser?.id,
                  timestamp: new Date().toISOString(),
                });
              }}
              aria-label="Show Smart Context"
              title="Show Smart Context"
            >
              <BotIcon />
            </SmartContextButton>
          )}

          {isCoachOrAdmin && !isAIFriendEnabled && (
            <IconContainer onClick={handleAction}>
              <StyledChannelPopoverIcon />
            </IconContainer>
          )}
        </EndActions>
      )}
      <ChannelActionMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        isPopperOpen={isPopperOpen}
        setIsPopperOpen={setIsPopperOpen}
        selectedBucket={selectedBucket()}
        channel={channel}
      />
      {children}
      <ChannelDocumentsModal
        isOpen={isDocumentsModalOpen}
        onClose={() => setIsDocumentsModalOpen(false)}
        channel={{ userId, id: channel.id }}
      />
    </Container>
  );
};

ChannelHeader.propTypes = {
  channel: PropTypes.instanceOf(ChatChannel).isRequired,
  children: PropTypes.node,
  isWebView: PropTypes.bool.isRequired,
};

ChannelHeader.defaultProps = {
  children: [],
};

export default compose(
  observer,
)(ChannelHeader);
